import EducationFilter, { EducationFilterResult } from "core/components/filter/components/educationFilter";
import PlaceFilter, { PlaceFilterResult } from "core/components/filter/components/placeFilter";
import Title from "core/components/titels/title";
import useUser from "core/hooks/useUser";
import UserFilterPermissions from "core/sharedmodels/userPermissions/userFilterPermissions";
import { Localizer } from "infrastructure/localization/localizer";
import { useCallback, useEffect, useState } from "react";
import useStamdataApi from "core/hooks/useStamdataApi";
import PlaceAndEducationCrudFilterRestrictModel from "core/sharedmodels/restrictModel/PlaceAndEducationCrudFilterRestrictModel";
import { ToRequestModelFromFileFilterModel } from "core/sharedmodels/restrictModel/PlaceAndEducationCrudFilterRestrictModel";
import { hasAnyDefinedProperties } from "index";

export const emptyEducationFilterValues: EducationFilterResult = {
    specialer: [],
    uddannelser: [],
};

export const emptyPlaceFilterValues: PlaceFilterResult = {
    regioner: [],
    omraader: [],
    afdelinger: [],
    praksiser: [],
    sygehuse: [],
    allRegionsSelected: false,
};

type FileFiltersType = {
    permissions: UserFilterPermissions;
    disable?: boolean;
    onEducationFilterResult: (result: EducationFilterResult) => void;
    onPlaceFilterResult: (result: PlaceFilterResult) => void;
    initialSelectedPlaceValues?: PlaceFilterResult;
    initialSelectedEducationValues?: EducationFilterResult;
}

const FileFilters = ({
    permissions,
    disable = false,
    onEducationFilterResult,
    onPlaceFilterResult,
    initialSelectedEducationValues,
    initialSelectedPlaceValues,
}: FileFiltersType) => {

    const { currentUser } = useUser();

    const [education, setEducation] = useState<EducationFilterResult>(initialSelectedEducationValues ?? emptyEducationFilterValues);
    const [place, setPlace] = useState<PlaceFilterResult>(initialSelectedPlaceValues ?? emptyPlaceFilterValues);

    const [restrictModel, setRestrictModel] = useState<PlaceAndEducationCrudFilterRestrictModel | undefined>();
    const { stamdataRestrictApi } = useStamdataApi();

    const handleEducationChange = useCallback((res: EducationFilterResult) => {
        setEducation(res);
        onEducationFilterResult(res);
    }, [onEducationFilterResult]);

    const handlePlaceChange = useCallback((res: PlaceFilterResult) => {
        setPlace(res);
        onPlaceFilterResult(res);
    }, [onPlaceFilterResult]);

    useEffect(() => {
        const fetchRestrict = async () => {
            const hasFilterToRestrict = hasAnyDefinedProperties({
                regioner: place.regioner,
                omraader: place.omraader,
                afdelinger: place.afdelinger,
                praksiser: place.praksiser,
                sygehuse: place.sygehuse,
                specialer: education.specialer,
                uddannelser: education.uddannelser, 
            });

            setRestrictModel(
                hasFilterToRestrict
                    ? await stamdataRestrictApi.getPlaceAndEducationRestrictions(
                        ToRequestModelFromFileFilterModel(education, place)
                    )
                    : undefined
            );
        };

        fetchRestrict();
    }, [place, education, stamdataRestrictApi]);



    return (
        <Title title={Localizer.filePage_fileRelationsTitle()} >
            <EducationFilter
                permissions={permissions}
                onChange={handleEducationChange}
                disable={disable}
                initialSelectedValues={initialSelectedEducationValues}
                restrictModel={restrictModel}
            />

            <PlaceFilter
                permissions={permissions}
                preselectViaPermissions={false}  //We shouldn't preselect via permissions, because some values shouldn't be set for specific roles (we use initialValues instead).
                onChange={handlePlaceChange}
                disable={disable}
                visualizationConfig={currentUser.IsAdminOrVus() ? undefined : {
                    showRegioner: currentUser.IsKonsulentForLaegeligVidereuddannelse(),
                    showOmraader: currentUser.IsKonsulentForLaegeligVidereuddannelse(),
                    showPraksiser: currentUser.IsUddannelsesansvarlig() || currentUser.IsSekretaer(),
                    showSygehuse: currentUser.IsLedelse(),
                    showAfdelinger: currentUser.IsUddannelsesansvarlig() || currentUser.IsSekretaer(),
                }}
                initialSelectedValues={initialSelectedPlaceValues}
                restrictModel={restrictModel}
            />

            <div className="margin-top-m subtle">
                <p className="small">{Localizer.filePage_uploadWarningMain()}</p>
                <p className="small">{Localizer.filePage_uploadWarningSub()}</p>
            </div>

        </Title>
    )
}

export default FileFilters;